import { ChangePasswordLoggedOutForm } from "components/Account/ChangePasswordLoggedOutForm";
import { PasswordRecoveryForm } from "components/Account/PasswordRecoveryForm";
import { RegisterForm } from "components/Account/RegisterForm";
import { SignInForm } from "components/Account/SignInForm";
import { CmsResponsiveImage } from "components/CmsResponsiveImage";
import Layout from "components/Layout";
import { Tab, TabPanel, Tabs } from "components/Tabs";
import { SiteTheme } from "components/siteContext";
import { readCMSDataServerSide } from "helpers/api";
import urls from "helpers/urls";
import { MenuData } from "interfaces";
import type { GetServerSideProps } from "next";
import { useRouter } from "next/router";

import { getNavData } from "../../[[...slug]]";

import styles from "./account-auth.module.scss";

const usableSlugs = [
    "register",
    "sign-in",
    "forgot-password",
    "change-password",
] as const;

type UsableSlug = (typeof usableSlugs)[number];

export const getServerSideProps: GetServerSideProps = async (context) => {
    try {
        await readCMSDataServerSide("/api/auth/account", { context });
        return {
            redirect: { destination: urls.accountOverview, permanent: false },
        };
    } catch (error) {}

    const navData = await getNavData(context);
    const { slug } = context.query;

    // It *feels* like TS should support this without the `any` cast, but it doesn't
    if (typeof slug !== "string" || !usableSlugs.includes(slug as any)) {
        return { notFound: true };
    }

    return {
        props: {
            menus: navData,
            activeTab: slug,
        },
    };
};

export interface Props {
    menus: MenuData[];
    activeTab: UsableSlug;
}

export default function AccountSignInPage({ menus, activeTab }: Props) {
    const router = useRouter();

    const siteTheme: SiteTheme = Object.values(SiteTheme).includes(
        process.env.NEXT_PUBLIC_SITE_THEME as SiteTheme,
    )
        ? (process.env.NEXT_PUBLIC_SITE_THEME as SiteTheme)
        : SiteTheme.EMTEK;

    const tabs: {
        [K in UsableSlug]: {
            title: string;
            tab: string;
            hidden: boolean;
            component: React.ReactNode;
        };
    } = {
        "register": {
            title: `Register for ${siteTheme}`,
            tab: "Create an Account",
            hidden: false,
            component: <RegisterForm />,
        },
        "sign-in": {
            title: `Sign in to ${siteTheme}`,
            tab: "Sign In",
            hidden: false,
            component: <SignInForm />,
        },
        "forgot-password": {
            title: "Forgot Password",
            tab: "Forgot Password",
            hidden: true,
            component: <PasswordRecoveryForm />,
        },
        "change-password": {
            title: "Change Password",
            tab: "Change Password",
            hidden: true,
            component: <ChangePasswordLoggedOutForm />,
        },
    };

    return (
        <Layout
            menus={menus}
            metadata={{
                title: tabs[activeTab]?.title ?? `${siteTheme} Authentication`,
            }}
        >
            <section className={styles.accountSignIn}>
                <aside className={styles.image}>
                    <CmsResponsiveImage
                        cmsImage={{
                            url: "https://res.cloudinary.com/emtek/image/upload/v1659369405/Palisades_tjrbqn.jpg",
                        }}
                        cldSrcSet={[
                            400, 800, 1200, 1600, 2000, 2400, 2800, 3200,
                        ]}
                        sizes="(max-width: 767px) 100vw, (min-width: 767) 50vw"
                        decorative={true}
                    />
                </aside>
                <div className={styles.content}>
                    <div className={styles.tabContainer}>
                        <Tabs
                            selectedKey={activeTab}
                            onSelectionChange={(selectedKey) => {
                                if (typeof selectedKey == "string") {
                                    router.push(selectedKey);
                                }
                            }}
                            tabs={usableSlugs.map((slug) => (
                                <Tab
                                    id={slug}
                                    key={slug}
                                    className={
                                        tabs[slug].hidden
                                            ? styles.hidden
                                            : undefined
                                    }
                                >
                                    {tabs[slug].tab}
                                </Tab>
                            ))}
                            panels={usableSlugs.map((slug) => (
                                <TabPanel
                                    className={styles.tabPanel}
                                    id={slug}
                                    key={slug}
                                >
                                    {tabs[slug].component}
                                </TabPanel>
                            ))}
                        ></Tabs>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
